import React, { useEffect, useState } from 'react';
import { 
  Line, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  CartesianGrid, 
  ComposedChart
} from 'recharts';
import { Activity, Zap, Wifi, Monitor, Globe, Shield, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../../api/client';

/** Same thresholds as the quality-over-time bar (do not retune without product sign-off). */
const STATUS_COLOR = {
  excellent: 'bg-emerald-500',
  good: 'bg-sky-400',
  fair: 'bg-amber-400',
  critical: 'bg-rose-500',
};

const STATUS_CHIP_CLASS = {
  excellent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  good: 'bg-sky-50 text-sky-700 border-sky-200',
  fair: 'bg-amber-50 text-amber-700 border-amber-200',
  critical: 'bg-rose-50 text-rose-700 border-rose-200',
};

/**
 * Normalizes connection type strings so that generation suffixes like
 * "4g" → "4G", "lte" → "LTE", "wifi" → "WiFi", "ethernet" → "Ethernet", etc.
 */
function normalizeConnectionType(raw) {
  if (!raw || raw === 'unknown') return 'unknown';
  const lower = String(raw).trim().toLowerCase();

  const generationMatch = lower.match(/^(\d+)g$/);
  if (generationMatch) return `${generationMatch[1]}G`;

  const technicalTerms = new Set(['lte', 'hspa', 'hspa+', 'edge', 'gprs', 'wimax', 'bluetooth']);
  if (technicalTerms.has(lower)) return lower.toUpperCase().replace('_', '+');

  const keyMap = {
    wifi: 'wifi',
    wi_fi: 'wifi',
    'wi-fi': 'wifi',
    ethernet: 'ethernet',
    cellular: 'cellular',
    none: 'none',
    'n/a': 'unknown',
  };

  return keyMap[lower] || lower;
}

function getPointStatus(fps, latency) {
  if (fps < 15 || latency > 600) return 'critical';
  if (fps < 25 || latency > 300) return 'fair';
  if (fps < 40 || latency > 150) return 'good';
  return 'excellent';
}

function getFpsStatus(fps) {
  if (fps >= 40) return 'excellent';
  if (fps >= 25) return 'good';
  if (fps >= 15) return 'fair';
  return 'critical';
}

function getLatencyStatus(latency) {
  if (latency <= 150) return 'excellent';
  if (latency <= 300) return 'good';
  if (latency <= 600) return 'fair';
  return 'critical';
}

function getBatteryStatus(level) {
  if (level >= 50) return 'excellent';
  if (level >= 20) return 'good';
  if (level > 0) return 'fair';
  return 'critical';
}

function getConnectionStatus(connection) {
  const key = String(connection || 'unknown').toLowerCase();
  if (key === 'wifi' || key === 'ethernet') return 'excellent';
  if (key === 'unknown' || key === 'none') return 'fair';
  return 'good';
}

/** Prefer majority band; escalate when slow/unstable samples are common. */
function getOverallVerdictKey(points) {
  if (!points.length) return 'excellent';
  const counts = { excellent: 0, good: 0, fair: 0, critical: 0 };
  points.forEach((p) => {
    if (counts[p.status] != null) counts[p.status] += 1;
  });
  const total = points.length;
  if (counts.critical / total >= 0.1) return 'critical';
  if (counts.fair / total >= 0.2) return 'fair';
  if (counts.good >= counts.excellent) return 'good';
  return 'excellent';
}

/** Show large delays in seconds so extreme values stay readable. */
function formatDelayMs(ms) {
  const n = Number(ms) || 0;
  if (n >= 1000) {
    const seconds = n / 1000;
    return {
      value: seconds >= 10 ? String(Math.round(seconds)) : seconds.toFixed(1),
      unit: 's',
    };
  }
  return { value: String(n), unit: 'ms' };
}

/**
 * Device & Connection Quality — plain-language telemetry for interview reports.
 */
export const TelemetryChart = ({ sessionId }) => {
  const { t } = useTranslation();
  const tr = (key, defaultValue, opts = {}) =>
    t(`interviewReport.technicalDiagnostic.${key}`, { defaultValue, ...opts });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ avgFps: 0, maxLatency: 0, lastBattery: 0, connection: 'unknown' });
  const [hoveredPoint, setHoveredPoint] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    async function fetchData() {
      try {
        const res = await api.get(`/sessions/${sessionId}/telemetry/`);
        const raw = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);

        if (raw.length > 0) {
          let firstValidIdx = 0;
          while (firstValidIdx < raw.length && !(raw[firstValidIdx].frame_rate > 0 || raw[firstValidIdx].latency_ms > 0)) {
            firstValidIdx++;
          }
          
          let lastValidIdx = raw.length - 1;
          while (lastValidIdx >= firstValidIdx && !(raw[lastValidIdx].frame_rate > 0 || raw[lastValidIdx].latency_ms > 0)) {
            lastValidIdx--;
          }

          const cleanedRaw = (firstValidIdx <= lastValidIdx) 
            ? raw.slice(firstValidIdx, lastValidIdx + 1) 
            : raw;

          const validFpsData = raw.filter(d => d.frame_rate > 0);
          const avgFps = validFpsData.length > 0 
            ? Math.round(validFpsData.reduce((sum, d) => sum + d.frame_rate, 0) / validFpsData.length)
            : 0;

          const maxLat = Math.max(...raw.map(d => d.latency_ms || 0));
          const lastValidBattery = [...raw].reverse().find(d => d.battery_level != null)?.battery_level || 0;
          const rawConn = [...raw].reverse().find(d => d.connection_type && d.connection_type !== 'unknown')?.connection_type || null;

          setStats({
            avgFps: avgFps || 0,
            maxLatency: maxLat,
            lastBattery: lastValidBattery,
            connection: normalizeConnectionType(rawConn),
          });

          const formatted = cleanedRaw.map((d) => {
            const date = new Date(d.timestamp);
            const fps = d.frame_rate || 0;
            const lat = d.latency_ms || 0;
            const battery = d.battery_level || 0;
            const status = getPointStatus(fps, lat);

            return {
              ...d,
              timeLabel: `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`,
              fps,
              latency: lat,
              battery,
              status,
              statusColor: STATUS_COLOR[status],
            };
          });
          setData(formatted);
        }
      } catch (err) {
        console.error('Failed to load telemetry', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [sessionId]);

  const statusLabel = (status) => {
    if (status === 'critical') return tr('critical', 'Needs attention');
    if (status === 'fair') return tr('fair', 'Fair');
    if (status === 'good') return tr('good', 'Good');
    return tr('excellent', 'Excellent');
  };

  const DiagnosticTooltip = ({ time, fps, latency, battery }) => (
    <div className="bg-[#121826] text-white p-4 rounded-xl shadow-2xl border border-white/10 font-poppins min-w-[220px] animate-in fade-in zoom-in duration-150">
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/10">
        <Clock size={16} className="text-slate-400" />
        <span className="text-sm font-bold tracking-tight">{time}</span>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-6">
          <span className="text-xs font-medium text-slate-400">{tr('fps', 'Video smoothness')}:</span>
          <span className="text-sm font-bold text-blue-400 text-right">
            {fps} <span className="text-[10px] font-medium text-slate-400">({tr('tooltipNormalFps', 'ideal 40+')})</span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-xs font-medium text-slate-400">{tr('latency', 'Connection delay')}:</span>
          <span className="text-sm font-bold text-indigo-400 text-right">
            {(() => { const d = formatDelayMs(latency); return `${d.value}${d.unit}`; })()}{' '}
            <span className="text-[10px] font-medium text-slate-400">({tr('tooltipNormalLatency', 'normal under 300 ms')})</span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-xs font-medium text-slate-400">{tr('battery', 'Battery remaining')}:</span>
          <span className="text-sm font-bold text-emerald-400 text-right">
            {battery}% <span className="text-[10px] font-medium text-slate-400">({tr('tooltipNormalBattery', 'normal above 20%')})</span>
          </span>
        </div>
      </div>
    </div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return <DiagnosticTooltip time={label} fps={item.fps} latency={item.latency} battery={item.battery} />;
    }
    return null;
  };

  if (loading) return (
    <div className="w-full h-40 flex items-center justify-center bg-slate-50 border border-dashed border-slate-200 rounded-xl font-poppins">
      <div className="flex items-center gap-2.5 animate-pulse text-indigo-500">
        <Activity size={24} />
        <span className="text-xs font-semibold tracking-wide">{tr('syncingData', 'Loading device & connection data...')}</span>
      </div>
    </div>
  );

  if (!data || data.length === 0) return null;

  const overallKey = getOverallVerdictKey(data);
  const overallCopy = {
    excellent: tr('overallExcellent', 'Overall: Excellent — the interview ran smoothly throughout.'),
    good: tr('overallGood', 'Overall: Good — mostly stable with a few slower moments.'),
    fair: tr('overallFair', 'Overall: Fair — there were noticeable slowdowns that may have affected the experience.'),
    critical: tr('overallCritical', 'Overall: Needs attention — connection or device issues were frequent during the interview.'),
  }[overallKey];

  const delayDisplay = formatDelayMs(stats.maxLatency);

  const kpis = [
    {
      labelKey: 'avgFps',
      defaultLabel: 'Video smoothness',
      value: stats.avgFps,
      unit: '',
      normalKey: 'normalFps',
      normalDefault: 'Ideal: 40+ (acceptable from 25)',
      status: getFpsStatus(stats.avgFps),
      icon: <Monitor className="w-4 h-4" />,
      color: 'from-blue-600 to-sky-500',
    },
    {
      labelKey: 'peakLatency',
      defaultLabel: 'Connection delay',
      value: delayDisplay.value,
      unit: delayDisplay.unit,
      normalKey: 'normalLatency',
      normalDefault: 'Normal: under 300 ms (ideal under 150 ms)',
      status: getLatencyStatus(stats.maxLatency),
      icon: <Wifi className="w-4 h-4" />,
      color: 'from-indigo-600 to-indigo-400',
    },
    {
      labelKey: 'batteryLevel',
      defaultLabel: 'Battery remaining',
      value: stats.lastBattery,
      unit: '%',
      normalKey: 'normalBattery',
      normalDefault: 'Normal: above 20%',
      status: getBatteryStatus(stats.lastBattery),
      icon: <Zap className="w-4 h-4" />,
      color: 'from-emerald-600 to-teal-400',
    },
    {
      labelKey: 'connection',
      defaultLabel: 'Internet type',
      value: stats.connection,
      unit: '',
      normalKey: 'normalConnection',
      normalDefault: 'Preferred: Wi‑Fi or Ethernet',
      status: getConnectionStatus(stats.connection),
      icon: <Globe className="w-4 h-4" />,
      color: 'from-slate-600 to-slate-400',
      isConnectionType: true,
    },
  ];

  return (
    <div className="w-full mt-8 rounded-xl p-4 sm:p-5 border border-indigo-100 bg-gradient-to-br from-indigo-50/30 to-white font-poppins transition-all pdf-page-break-inside-avoid shadow-sm print:shadow-none">
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-2.5 rounded-xl shadow-md shrink-0">
          <Activity className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-900 leading-tight">{tr('title', 'Device & Connection Quality')}</h2>
          <p className="text-slate-500 text-xs mt-0.5 font-medium">{tr('subtitle', "How smoothly the candidate’s camera, device, and internet worked during the interview")}</p>
        </div>
      </div>

      <div className={`mb-5 px-3.5 py-2.5 rounded-lg border text-sm font-medium ${STATUS_CHIP_CLASS[overallKey]}`}>
        {overallCopy}
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-stretch">
        <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 lg:grid-rows-4 gap-3 lg:w-64 w-full">
          {kpis.map((kpi, i) => (
            <div key={i} className="flex flex-col p-3.5 bg-white border border-slate-100 rounded-xl shadow-sm transition-all hover:border-indigo-200">
              <div className="flex items-start gap-2.5">
                <div className={`p-1.5 rounded-md bg-gradient-to-br ${kpi.color} text-white shrink-0`}>
                  {kpi.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[11px] font-semibold text-slate-500 leading-snug">
                    {tr(kpi.labelKey, kpi.defaultLabel)}
                  </span>
                  <span className={`inline-block mt-1 text-[9px] font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${STATUS_CHIP_CLASS[kpi.status]}`}>
                    {statusLabel(kpi.status)}
                  </span>
                </div>
              </div>
              <div className="mt-auto pt-2">
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-bold text-slate-900 leading-none">
                    {kpi.isConnectionType 
                      ? tr(`connectionTypes.${kpi.value}`, String(kpi.value).replace(/\b\w/g, c => c.toUpperCase())) 
                      : kpi.value}
                  </span>
                  {kpi.unit ? (
                    <span className="text-[11px] font-semibold text-slate-400">{kpi.unit}</span>
                  ) : null}
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mt-1">{tr(kpi.normalKey, kpi.normalDefault)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex-1 w-full bg-white border border-slate-100 rounded-xl p-5 shadow-sm relative overflow-visible flex flex-col justify-between min-h-0">
          <div>
            <div className="flex items-center justify-between mb-3 px-1 gap-2 flex-wrap">
               <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                  {tr('sessionIntegrityPulse', 'Quality over time')}
               </h4>
               <span className="text-[9px] font-semibold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-100 tracking-wide">
                  {tr('samplePointsCaptured', '{{count}} checks recorded', { count: data.length })}
               </span>
            </div>
            
            <div className="flex h-14 w-full bg-slate-50 border border-slate-200 p-1.5 group/heatmap relative rounded-lg">
              {data.map((point, i) => (
                <div 
                  key={i} 
                  className={`h-full flex-1 mx-[0.25px] transition-all hover:scale-y-125 hover:z-20 cursor-crosshair ${point.statusColor} relative 
                    ${i === 0 ? 'rounded-l-sm' : ''} 
                    ${i === data.length - 1 ? 'rounded-r-sm' : ''}`}
                  onMouseEnter={() => setHoveredPoint(point)}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              ))}
              
              {hoveredPoint && (
                <div className="absolute top-[-130px] left-1/2 -translate-x-1/2 z-50 pointer-events-none drop-shadow-2xl">
                   <DiagnosticTooltip 
                      time={hoveredPoint.timeLabel} 
                      fps={hoveredPoint.fps} 
                      latency={hoveredPoint.latency} 
                      battery={hoveredPoint.battery} 
                    />
                    <div className="w-4 h-4 bg-[#121826] rotate-45 mx-auto -mt-2 border-r border-b border-white/10" />
                </div>
              )}
            </div>
            
            <div className="flex justify-between mt-2.5 px-1">
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-semibold text-slate-400 tracking-wide">{tr('start', 'Start')}: {data[0]?.timeLabel}</span>
                <Clock size={10} className="text-slate-300" />
              </div>
              <div className="flex items-center gap-2">
                <Clock size={10} className="text-slate-300" />
                <span className="text-[9px] font-semibold text-slate-400 tracking-wide">{tr('end', 'End')}: {data[data.length-1]?.timeLabel}</span>
              </div>
            </div>
          </div>

          <div className="my-2 flex-1 flex flex-col justify-end min-h-[110px]">
            <div className="flex items-center justify-between mb-1.5 px-1">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{tr('videoSmoothnessTrend', 'Video FPS Stability')}</span>
              <span className="text-[9px] font-semibold text-slate-400 tracking-wide">{stats.avgFps} FPS avg</span>
            </div>
            <div className="w-full h-24 sm:h-28 opacity-80 transition-all duration-300 hover:opacity-100">
               <ResponsiveContainer width="100%" height="100%">
                 <ComposedChart data={data} margin={{ top: 6, right: 0, left: -40, bottom: 0 }}>
                   <CartesianGrid vertical={false} strokeDasharray="6 6" stroke="#f1f5f9" />
                   <XAxis dataKey="timeLabel" hide />
                   <YAxis fontSize={8} fontWeight={700} stroke="#94a3b8" tickLine={false} axisLine={false} domain={[0, 65]} width={40} />
                   <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
                   <Area type="monotone" dataKey="fps" stroke="#6366f1" strokeWidth={1} fill="#e0e7ff" isAnimationActive={false} opacity={0.25} />
                   <Line type="monotone" dataKey="fps" stroke="#4f46e5" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                 </ComposedChart>
               </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-auto pt-3 flex flex-wrap items-center justify-between gap-2.5 border-t border-slate-100">
            <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-sm" />
                <span className="text-[10px] font-semibold text-slate-600">{tr('excellent', 'Excellent')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-sky-400 shadow-sm" />
                <span className="text-[10px] font-semibold text-slate-600">{tr('good', 'Good')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <span className="text-[10px] font-semibold text-slate-600">{tr('fair', 'Fair')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-sm shadow-rose-200" />
                <span className="text-[10px] font-semibold text-slate-600">{tr('critical', 'Needs attention')}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 px-2.5 py-1 bg-slate-50 rounded-lg border border-slate-100">
              <Shield className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
              <span className="text-[9px] font-semibold text-slate-500 tracking-wide">{tr('hardwarePulseAuthenticated', 'Captured automatically during the interview')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
